import { apiClient } from "./client";
import type {
  Alert,
  AlertSummary,
  AuditLog,
  Building,
  BuildingDashboard,
  BuildingSummary,
  CsvImportResult,
  Invoice,
  Lease,
  Meter,
  NotificationPreference,
  OrgDashboard,
  Organisation,
  OrganisationSummary,
  Paginated,
  Reading,
  RolePermissions,
  SearchResults,
  StaffDashboard,
  Tariff,
  Unit,
  UnitDashboard,
  User,
  UUID,
  UtilityType,
} from "@/types";

const v1 = "/api/v1";

export const authApi = {
  exchange: async (azureToken: string, tenantType: "customer" | "staff") =>
    (await apiClient.post(`${v1}/auth/token`, { azure_token: azureToken, tenant_type: tenantType })).data,
  me: async (): Promise<User> => (await apiClient.get(`${v1}/auth/me`)).data,
  inviteInfo: async (token: string) =>
    (await apiClient.get(`${v1}/auth/invite-info`, { params: { token } })).data,
};

export const orgApi = {
  list: async (): Promise<OrganisationSummary[]> => (await apiClient.get(`${v1}/organisations`)).data,
  get: async (id: UUID): Promise<Organisation> => (await apiClient.get(`${v1}/organisations/${id}`)).data,
  create: async (payload: Partial<Organisation>): Promise<Organisation> =>
    (await apiClient.post(`${v1}/organisations`, payload)).data,
  update: async (id: UUID, payload: Partial<Organisation>): Promise<Organisation> =>
    (await apiClient.patch(`${v1}/organisations/${id}`, payload)).data,
};

export const buildingApi = {
  list: async (orgId: UUID): Promise<BuildingSummary[]> =>
    (await apiClient.get(`${v1}/organisations/${orgId}/buildings`)).data,
  get: async (orgId: UUID, id: UUID): Promise<Building> =>
    (await apiClient.get(`${v1}/organisations/${orgId}/buildings/${id}`)).data,
  create: async (orgId: UUID, payload: Partial<Building>): Promise<Building> =>
    (await apiClient.post(`${v1}/organisations/${orgId}/buildings`, payload)).data,
  update: async (orgId: UUID, id: UUID, payload: Partial<Building>): Promise<Building> =>
    (await apiClient.patch(`${v1}/organisations/${orgId}/buildings/${id}`, payload)).data,
};

export const unitApi = {
  list: async (buildingId: UUID, commonAreasOnly = false): Promise<Unit[]> =>
    (await apiClient.get(`${v1}/buildings/${buildingId}/units`, {
      params: { common_areas_only: commonAreasOnly },
    })).data,
  get: async (buildingId: UUID, id: UUID): Promise<Unit> =>
    (await apiClient.get(`${v1}/buildings/${buildingId}/units/${id}`)).data,
  create: async (buildingId: UUID, payload: Partial<Unit>): Promise<Unit> =>
    (await apiClient.post(`${v1}/buildings/${buildingId}/units`, payload)).data,
  update: async (buildingId: UUID, id: UUID, payload: Partial<Unit>): Promise<Unit> =>
    (await apiClient.patch(`${v1}/buildings/${buildingId}/units/${id}`, payload)).data,
};

export const leaseApi = {
  listForUnit: async (unitId: UUID): Promise<Lease[]> =>
    (await apiClient.get(`${v1}/leases/units/${unitId}`)).data,
  get: async (id: UUID): Promise<Lease> => (await apiClient.get(`${v1}/leases/${id}`)).data,
  create: async (unitId: UUID, payload: Partial<Lease>): Promise<Lease> =>
    (await apiClient.post(`${v1}/leases/units/${unitId}`, payload)).data,
  update: async (id: UUID, payload: Partial<Lease>): Promise<Lease> =>
    (await apiClient.patch(`${v1}/leases/${id}`, payload)).data,
  delete: async (id: UUID): Promise<{ message: string }> =>
    (await apiClient.delete(`${v1}/leases/${id}`)).data,
  expiring: async (orgId: UUID, withinDays = 60): Promise<Lease[]> =>
    (await apiClient.get(`${v1}/leases/organisations/${orgId}/expiring`, {
      params: { within_days: withinDays },
    })).data,
};

export const meterApi = {
  list: async (unitId: UUID): Promise<Meter[]> =>
    (await apiClient.get(`${v1}/units/${unitId}/meters`)).data,
  get: async (unitId: UUID, id: UUID): Promise<Meter> =>
    (await apiClient.get(`${v1}/units/${unitId}/meters/${id}`)).data,
  create: async (unitId: UUID, payload: Partial<Meter>): Promise<Meter> =>
    (await apiClient.post(`${v1}/units/${unitId}/meters`, payload)).data,
  update: async (unitId: UUID, id: UUID, payload: Partial<Meter>): Promise<Meter> =>
    (await apiClient.patch(`${v1}/units/${unitId}/meters/${id}`, payload)).data,
};

export const readingApi = {
  submit: async (payload: {
    meter_id: UUID;
    reading_date: string;
    raw_value: number;
    unit_of_measure?: string;
    notes?: string;
  }): Promise<Reading> => (await apiClient.post(`${v1}/readings`, payload)).data,
  uploadCsv: async (file: File): Promise<CsvImportResult> => {
    const form = new FormData();
    form.append("file", file);
    return (
      await apiClient.post(`${v1}/readings/csv`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  history: async (meterId: UUID, page = 1, pageSize = 50): Promise<Paginated<Reading>> =>
    (await apiClient.get(`${v1}/readings/meters/${meterId}`, {
      params: { page, page_size: pageSize },
    })).data,
  trend: async (meterId: UUID, months = 6) =>
    (await apiClient.get(`${v1}/readings/meters/${meterId}/trend`, {
      params: { months },
    })).data,
};

export const alertApi = {
  forOrg: async (orgId: UUID, unresolvedOnly = false): Promise<Alert[]> =>
    (await apiClient.get(`${v1}/alerts/organisations/${orgId}`, {
      params: { unresolved_only: unresolvedOnly },
    })).data,
  summaryForOrg: async (orgId: UUID): Promise<AlertSummary> =>
    (await apiClient.get(`${v1}/alerts/organisations/${orgId}/summary`)).data,
  forBuilding: async (buildingId: UUID, unresolvedOnly = false): Promise<Alert[]> =>
    (await apiClient.get(`${v1}/alerts/buildings/${buildingId}`, {
      params: { unresolved_only: unresolvedOnly },
    })).data,
  resolve: async (alertId: UUID): Promise<Alert> =>
    (await apiClient.post(`${v1}/alerts/${alertId}/resolve`)).data,
};

export const userApi = {
  listForOrg: async (orgId: UUID): Promise<User[]> =>
    (await apiClient.get(`${v1}/users/organisations/${orgId}`)).data,
  invite: async (payload: {
    email: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    role_id: UUID;
    organisation_id: UUID;
    building_ids?: UUID[];
    unit_ids?: UUID[];
  }): Promise<User> => (await apiClient.post(`${v1}/users/invite`, payload)).data,
  update: async (userId: UUID, payload: Partial<User & { role_id?: UUID }>): Promise<User> =>
    (await apiClient.patch(`${v1}/users/${userId}`, payload)).data,
  setBuildingAccess: async (userId: UUID, building_ids: UUID[]) =>
    (await apiClient.put(`${v1}/users/${userId}/building-access`, { building_ids })).data,
  setUnitAccess: async (userId: UUID, unit_ids: UUID[]) =>
    (await apiClient.put(`${v1}/users/${userId}/unit-access`, { unit_ids })).data,
  resendInvite: async (userId: UUID): Promise<{ message: string }> =>
    (await apiClient.post(`${v1}/users/${userId}/resend-invite`)).data,
  delete: async (userId: UUID): Promise<{ message: string }> =>
    (await apiClient.delete(`${v1}/users/${userId}`)).data,
};

export const searchApi = {
  query: async (q: string): Promise<SearchResults> =>
    (await apiClient.get(`${v1}/search`, { params: { q } })).data,
};

export const staffViewsApi = {
  meters: async (params: Record<string, string> = {}) =>
    (await apiClient.get(`${v1}/staff/meters`, { params })).data,
  metersSummary: async () => (await apiClient.get(`${v1}/staff/meters/summary`)).data,
  buildings: async () => (await apiClient.get(`${v1}/staff/buildings`)).data,
  users: async () => (await apiClient.get(`${v1}/staff/users`)).data,
  alerts: async (unresolved_only = false) =>
    (await apiClient.get(`${v1}/staff/alerts`, { params: { unresolved_only } })).data,
  readingsActivity: async () => (await apiClient.get(`${v1}/staff/readings/activity`)).data,
};

export const roleApi = {
  list: async (orgId: UUID): Promise<RolePermissions[]> =>
    (await apiClient.get(`${v1}/organisations/${orgId}/roles`)).data,
  create: async (orgId: UUID, payload: Partial<RolePermissions>): Promise<RolePermissions> =>
    (await apiClient.post(`${v1}/organisations/${orgId}/roles`, payload)).data,
  update: async (orgId: UUID, id: UUID, payload: Partial<RolePermissions>): Promise<RolePermissions> =>
    (await apiClient.patch(`${v1}/organisations/${orgId}/roles/${id}`, payload)).data,
  delete: async (orgId: UUID, id: UUID): Promise<void> => {
    await apiClient.delete(`${v1}/organisations/${orgId}/roles/${id}`);
  },
};

export const dashboardApi = {
  staff: async (): Promise<StaffDashboard> => (await apiClient.get(`${v1}/dashboard/staff`)).data,
  org: async (orgId: UUID): Promise<OrgDashboard> =>
    (await apiClient.get(`${v1}/dashboard/org/${orgId}`)).data,
  building: async (buildingId: UUID): Promise<BuildingDashboard> =>
    (await apiClient.get(`${v1}/dashboard/building/${buildingId}`)).data,
  unit: async (unitId: UUID): Promise<UnitDashboard> =>
    (await apiClient.get(`${v1}/dashboard/unit/${unitId}`)).data,
};

export const exportApi = {
  readingsCsv: (meterId: UUID, fromDate?: string, toDate?: string) =>
    `${apiClient.defaults.baseURL}${v1}/export/readings?meter_id=${meterId}` +
    (fromDate ? `&from_date=${fromDate}` : "") +
    (toDate ? `&to_date=${toDate}` : ""),
  reportCsv: (orgId: UUID, month: string, buildingId?: UUID) =>
    `${apiClient.defaults.baseURL}${v1}/export/report/${orgId}?month=${month}` +
    (buildingId ? `&building_id=${buildingId}` : ""),
};

export const tariffApi = {
  list: async (orgId: UUID): Promise<Tariff[]> =>
    (await apiClient.get(`${v1}/organisations/${orgId}/tariffs`)).data,
  create: async (orgId: UUID, payload: {
    utility_type: UtilityType;
    rate_per_unit: number;
    effective_from: string;
    effective_to?: string | null;
    label?: string;
  }): Promise<Tariff> =>
    (await apiClient.post(`${v1}/organisations/${orgId}/tariffs`, payload)).data,
  update: async (orgId: UUID, id: UUID, payload: Partial<Tariff>): Promise<Tariff> =>
    (await apiClient.patch(`${v1}/organisations/${orgId}/tariffs/${id}`, payload)).data,
  delete: async (orgId: UUID, id: UUID) => {
    await apiClient.delete(`${v1}/organisations/${orgId}/tariffs/${id}`);
  },
};

export const invoiceApi = {
  listForUnit: async (unitId: UUID): Promise<Invoice[]> =>
    (await apiClient.get(`${v1}/invoices/units/${unitId}`)).data,
  generate: async (payload: { unit_id: UUID; period_start: string; period_end: string }): Promise<Invoice> =>
    (await apiClient.post(`${v1}/invoices/generate`, payload)).data,
  get: async (id: UUID): Promise<Invoice> => (await apiClient.get(`${v1}/invoices/${id}`)).data,
  issue: async (id: UUID): Promise<Invoice> => (await apiClient.post(`${v1}/invoices/${id}/issue`)).data,
};

export const auditApi = {
  list: async (
    orgId: UUID,
    params: { page?: number; page_size?: number; from_date?: string; to_date?: string; action?: string; actor_user_id?: UUID } = {},
  ): Promise<Paginated<AuditLog>> =>
    (await apiClient.get(`${v1}/audit/organisations/${orgId}`, { params })).data,
};

export const notificationApi = {
  list: async (): Promise<NotificationPreference[]> =>
    (await apiClient.get(`${v1}/notifications/preferences`)).data,
  update: async (preferences: { alert_type: string; email_enabled: boolean }[]) =>
    (await apiClient.put(`${v1}/notifications/preferences`, { preferences })).data,
};
